import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { fmtPrice, minPrice, floorLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Img, SIZES } from "@/components/shared/Img";
import { useImageAlts } from "@/hooks/use-image-alts";

const capacityText = (room) => (room.capacity ? `${room.capacity} personar` : room.capacity_note || "");

/** Poster-style room tile: photo with the name set on top of it. */
export const RoomCard = ({ room, className, index, aspect = "aspect-[4/5] sm:aspect-[3/4]" }) => {
  const price = minPrice(room);
  const closed = room.status === "closed";
  const { altFor } = useImageAlts();
  return (
    <Link to={`/rom/${room.slug}`} className={cn("group relative block overflow-hidden rounded-[12px] bg-ink text-paper", className)} data-testid={`room-card-${room.slug}`}>
      <figure className={cn("img-hover w-full", aspect)}>
        {room.images?.[0] && <Img src={room.images[0]} alt={room.captions?.[room.images[0]] || altFor(room.images[0], room.name)} width={1200} sizes={SIZES.half} className={cn("h-full w-full object-cover", closed && "grayscale")} />}
      </figure>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/15 to-ink/5" />
      {index !== undefined && <span className="absolute left-5 top-4 t-mono text-paper/80 tabular">{String(index + 1).padStart(2, "0")}</span>}
      {closed && <span className="absolute right-4 top-4 rounded-[6px] bg-paper px-2.5 py-1 t-mono-sm text-ink">Mellombels stengt</span>}
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 sm:p-6">
        <div className="min-w-0">
          <h3 className="font-display text-[clamp(22px,1.9vw,30px)] font-semibold leading-[1] tracking-[-0.03em] text-paper">{room.name}</h3>
          <p className="mt-2 text-[14px] text-paper/80">{floorLabel(room.floor)}{capacityText(room) ? ` · ${capacityText(room)}` : ""}</p>
        </div>
        <span className="shrink-0 rounded-[8px] bg-paper px-3 py-1.5 text-[13px] font-medium text-ink tabular transition-colors group-hover:bg-sol">{price ? `frå ${fmtPrice(price)}` : closed ? "Stengt" : "Etter avtale"}</span>
      </div>
    </Link>
  );
};

/** Grid of poster tiles. */
export const RoomGrid = ({ rooms = [], testId = "room-grid", className }) => (
  <div className={cn("grid gap-4 sm:grid-cols-2 lg:grid-cols-3", className)} data-testid={testId}>
    {rooms.map((r, i) => <RoomCard key={r.id} room={r} index={i} aspect="aspect-[4/3] sm:aspect-[4/5]" />)}
  </div>
);

/** Horizontal snap scroller of room tiles. */
export const RoomScroller = ({ rooms = [], testId = "room-scroller" }) => {
  const ref = useRef(null);
  const go = (dir) => ref.current?.scrollBy({ left: dir * Math.max(320, ref.current.clientWidth * 0.7), behavior: "smooth" });
  return (
    <div data-testid={testId}>
      <div ref={ref} className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 sm:-mx-8 sm:px-8 lg:-mx-10 lg:px-10">
        {rooms.map((r, i) => (
          <div key={r.id} className="w-[78vw] shrink-0 snap-start sm:w-[360px] lg:w-[400px]">
            <RoomCard room={r} index={i} />
          </div>
        ))}
      </div>
      <div className="mt-6 hidden gap-2 lg:flex">
        <button type="button" onClick={() => go(-1)} className="flex h-11 w-11 items-center justify-center rounded-[8px] border border-foreground/20 transition-colors hover:bg-foreground hover:text-background" aria-label="Bla bakover" data-testid={`${testId}-prev`}><ArrowLeft className="h-4 w-4" /></button>
        <button type="button" onClick={() => go(1)} className="flex h-11 w-11 items-center justify-center rounded-[8px] border border-foreground/20 transition-colors hover:bg-foreground hover:text-background" aria-label="Bla framover" data-testid={`${testId}-next`}><ArrowRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export const RoomIndex = RoomGrid;
